import { CssVarsThemeOptions, Theme } from "@mui/material/styles";
import { getSoftPrimaryTreatment } from "@themes/utils/button";
import { paletteShade } from "@themes/utils/paletteShade";

type ThemeComponents = NonNullable<CssVarsThemeOptions["components"]>;

/**
 * Every glyph inside a button renders at `controls.iconSize`, including icons
 * passed in through props. `data-icon` marks the `Icon` atom, so spinners and
 * badges keep their size; the second selector covers animated glyphs.
 */
export const buttonIconSx = (theme: Theme) => ({
  "& svg[data-icon], & [data-icon] svg": {
    height: theme.controls.iconSize,
    width: theme.controls.iconSize
  }
});

/** A toggle segment's corner — see `controls.segment`. */
const segmentRadius = (theme: Theme) => `${theme.controls.segment.radius}px`;

export const createButtonOverrides = (): ThemeComponents => ({
  MuiButton: {
    styleOverrides: {
      /**
       * The dark scheme's primary outlined button, and nothing else.
       *
       * MUI labels and edges an outlined button in `palette[color].main`. Our
       * dark ramps put primary's `main` at their DARKEST step, so a primary
       * outlined button reads 1.02-1.10:1 against the dark page - label and
       * border both effectively invisible. `primary.600` is the lightest step
       * all five palettes share: 8.35-12.31:1 for the label and 3.14-4.12:1
       * for the 50% border, on `background.default` and `background.paper`
       * alike.
       *
       * Deliberately narrow. The previous blanket `outlined` override also
       * recoloured `error`/`warning`/`success`/`info`, whose own palette
       * colours are correct on both schemes; this one returns nothing for
       * them, and emits only inside `applyStyles("dark")`, so the light
       * scheme keeps stock MUI everywhere.
       *
       * It re-points the two CSS variables MUI reads rather than setting
       * `color`/`borderColor` outright. That keeps it under `.Mui-disabled`,
       * which sets those two properties directly and so still wins on
       * specificity - disabled semantics need no `!important` to survive.
       */
      outlined: ({ ownerState, theme }) => {
        if (ownerState.color !== "primary") return {};

        const edge = paletteShade(theme, "primary", 600);
        return theme.applyStyles("dark", {
          // MUI's own hover rule lives in this media query; match it so ours
          // lands beside it rather than fighting it on a different surface.
          "@media (hover: hover)": {
            "&:hover": { "--variant-outlinedBorder": edge }
          },
          "--variant-outlinedBorder": `color-mix(in srgb, ${edge} 50%, transparent)`,
          "--variant-outlinedColor": edge
        });
      },
      root: ({ theme }) => ({
        ...buttonIconSx(theme),
        "&:active": {
          boxShadow: "none"
        },
        "&:hover": {
          boxShadow: "none"
        },
        borderRadius: `${theme.shape.borderRadiusButton}px`,
        boxShadow: "none",
        // Border-box min height: borders sit inside it, so every variant matches,
        // and a wrapping label can still grow.
        boxSizing: "border-box",
        fontSize: theme.typography.button.fontSize,
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: "normal",
        minHeight: theme.controls.height,
        padding: `0 ${theme.controls.paddingX}px`,
        textTransform: "none"
      }),
      text: ({ theme }) => ({
        "&:hover": {
          backgroundColor: "transparent",
          transform: "scale(1.01)"
        },
        color: theme.vars.palette.action.selected,
        transform: "scale(1)",
        transition: "transform 0.2s ease-in-out"
      })
    }
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...buttonIconSx(theme),
        // The control corner, not MUI's circle.
        borderRadius: theme.shape.borderRadiusButton
      })
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-selected": {
          "&:hover": {
            backgroundColor: theme.vars.palette.primary.main,
            color: theme.vars.palette.primary.contrastText
          },
          backgroundColor: theme.vars.palette.primary.main,
          color: theme.vars.palette.primary.contrastText
        },
        borderRadius: 6,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.fontWeightMedium,
        marginBottom: 2,
        width: "100%"
      })
    }
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => {
        const soft = getSoftPrimaryTreatment(theme);
        const selectedSoft = {
          background: soft.background,
          border: `2px solid ${soft.ring}`,
          borderRadius: segmentRadius(theme),
          color: soft.text
        };

        return {
          ...buttonIconSx(theme),
          "&.Mui-selected": {
            "&:active": { background: soft.active },
            "&:hover": { background: soft.hover },
            ...selectedSoft
          },
          // BaseToggleButtons disables the ACTIVE button (to block
          // re-selecting), so selected+disabled is its normal resting state —
          // it must look identical to selected. Without this rule MUI's
          // generic `.Mui-disabled` styling can win on plain specificity ties
          // (stylesheet order differs per mount), washing the button out. The
          // doubled pseudo-class outranks both single-class rules.
          "&.Mui-selected.Mui-disabled": { ...selectedSoft, opacity: 1 },
          "&:not(.Mui-selected)": {
            "&:hover": {
              background: theme.vars.palette.surface.inset,
              borderRadius: segmentRadius(theme)
            },
            background: "transparent",
            // Transparent, not `none`: the selected pill draws a 2px border, so
            // dropping it here made an unselected pill 4px shorter and narrower.
            // The group sizes to its tallest child, so its own height changed
            // with the selection (41px on a text pill, 38px on an icon one).
            border: "2px solid transparent",
            color: theme.vars.palette.text.secondary
          },
          borderRadius: segmentRadius(theme),
          boxSizing: "border-box",
          height: theme.controls.segment.height,
          lineHeight: 1,
          padding: "0 12px",
          whiteSpace: "nowrap"
        };
      }
    }
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      firstButton: ({ theme }) => ({
        borderRadius: segmentRadius(theme)
      }),

      lastButton: ({ theme }) => ({
        "&.Mui-disabled": {
          borderRadius: segmentRadius(theme)
        },
        borderRadius: segmentRadius(theme)
      }),

      middleButton: ({ theme }) => ({
        "&.Mui-disabled": {
          borderRadius: segmentRadius(theme)
        },
        borderRadius: segmentRadius(theme)
      }),

      root: ({ theme }) => ({
        borderRadius: `${theme.shape.borderRadiusButton}px`,
        boxSizing: "border-box",
        gap: 4,
        height: theme.controls.height,
        padding: theme.controls.segment.inset,
        width: "fit-content"
      })
    }
  }
});
